import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";
import { listBuyerMessages, shipWithNote } from "../lib/buyer-messages";
import { rememberCarrier, trackingUrl } from "../lib/carriers";
import TrackingFields from "../components/orders/TrackingFields";
import OrderTasks, { type OrderTasksMap } from "../components/orders/OrderTasks";
import CustomOrderPrint from "../components/orders/CustomOrderPrint";
import OrderDesignFiles, { type LinkedDesign, type SupplierInfo } from "../components/orders/OrderDesignFiles";
import OrderProfitSection from "../components/orders/OrderProfitSection";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ShoppingCart, Loader2, RefreshCw, Eye, ChevronLeft, ChevronRight,
  Filter, AlertCircle, Truck, Package, DollarSign, Clock,
  CheckCircle, XCircle, MapPin, User, Send, ExternalLink, Search,
  Gift, Mail, CreditCard, Copy, ImageIcon, Hash,
  FileText, Calendar, RotateCcw, Store,
} from "lucide-react";
import { clsx } from "clsx";

// ============================================
// Types (Etsy v3 receipt + backend enrichment)
// ============================================

interface Money { amount: number; divisor: number; currency_code: string }

interface EtsyTransaction {
  transaction_id: number;
  title: string;
  description?: string;
  quantity: number;
  price: Money;
  shipping_cost?: Money;
  listing_id: number;
  product_id?: number;
  sku?: string | null;
  is_digital?: boolean;
  transaction_type?: string;
  paid_timestamp?: number | null;
  shipped_timestamp?: number | null;
  expected_ship_date?: number | null;
  min_processing_days?: number | null;
  max_processing_days?: number | null;
  shipping_method?: string | null;
  shipping_upgrade?: string | null;
  variations?: Array<{ property_id?: number; formatted_name: string; formatted_value: string }>;
  product_data?: Array<{ property_id?: number; property_name: string; values?: string[] }>;
  buyer_coupon?: number;
  shop_coupon?: number;
  // backend enrichment
  image_url?: string | null;
  image_thumb_url?: string | null;
  listing_url?: string | null;
  listing_state?: string | null;
  listing_title?: string | null;
  /** Siparis anindaki kargo profili (ad + cikis yeri) */
  shipping_profile?: { id: string; title: string; origin: string | null } | null;
  listing_num_favorers?: number | null;
  design?: LinkedDesign | null;
  designAlts?: LinkedDesign[];
  /** Big print listeden gelen siparis (16 inc baski dosyasi) */
  bigPrint?: boolean;
  supplier?: SupplierInfo | null;
}

interface EtsyShipment {
  receipt_shipping_id?: number;
  shipment_notification_timestamp?: number;
  carrier_name: string;
  tracking_code: string;
}

interface EtsyReceipt {
  receipt_id: number;
  receipt_type?: number;
  buyer_user_id: number;
  buyer_email: string;
  name: string;
  first_line: string;
  second_line: string;
  city: string;
  state: string;
  zip: string;
  country_iso: string;
  formatted_address?: string;
  status?: string;
  payment_method?: string;
  payment_email?: string;
  grandtotal: Money;
  subtotal: Money;
  total_shipping_cost: Money;
  total_tax_cost: Money;
  total_vat_cost?: Money;
  discount_amt?: Money;
  gift_wrap_price?: Money;
  was_paid: boolean;
  was_shipped: boolean;
  was_delivered: boolean;
  was_canceled: boolean;
  is_paid?: boolean;
  is_shipped?: boolean;
  is_gift: boolean;
  gift_message: string;
  gift_sender?: string;
  message_from_buyer: string;
  message_from_seller?: string;
  create_timestamp: number;
  update_timestamp: number;
  transactions: EtsyTransaction[];
  shipments?: EtsyShipment[];
  refunds?: Array<{ amount: Money; created_timestamp: number; reason?: string; note_from_issuer?: string; status?: string }>;
  // backend enrichment
  etsy_order_url?: string;
  // Sunucuda bir kez kaydedilen karlilik (yoksa henuz cekilmedi)
  profit?: { currency: string; etsyEarned: number; netProfit: number | null; margin: number | null } | null;
  // Mesaj gorevleri (sunucudaki taslaklar)
  tasks?: OrderTasksMap;
}

interface EtsyReceiptsResponse {
  count: number;
  results: EtsyReceipt[];
}

type OrderFilter = "all" | "due" | "pending" | "shipped" | "completed" | "canceled";

const FILTER_OPTIONS: Array<{ value: OrderFilter; label: string }> = [
  { value: "all", label: "Tümü" },
  { value: "due", label: "Bugün tamamlanacak" },
  { value: "pending", label: "Bekleyen" },
  { value: "shipped", label: "Gönderildi" },
  { value: "completed", label: "Tamamlandı" },
  { value: "canceled", label: "İptal" },
];




async function fetchReceipts(filter: OrderFilter, offset: number, limit: number): Promise<EtsyReceiptsResponse> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  if (filter === "pending" || filter === "due") { params.set("was_paid", "true"); params.set("was_shipped", "false"); }
  if (filter === "shipped") { params.set("was_shipped", "true"); params.set("was_delivered", "false"); }
  if (filter === "completed") { params.set("was_delivered", "true"); }
  if (filter === "canceled") { params.set("was_canceled", "true"); }

  const res = await fetch(`${getBackendUrl()}/api/etsy/receipts?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Siparişler alınamadı");
  const data = await res.json();
  return data.data;
}


// ============================================
// Helpers
// ============================================

function formatPrice(price?: Money | null) {
  if (!price || typeof price.amount !== "number") return "-";
  return `${(price.amount / (price.divisor || 100)).toFixed(2)} ${price.currency_code || ""}`.trim();
}

function priceValue(price?: Money | null) {
  if (!price || typeof price.amount !== "number") return 0;
  return price.amount / (price.divisor || 100);
}

// Satirdaki net kar: sunucudaki kayitli snapshot'tan (her listede Etsy'ye gidilmez)
function ProfitBadge({ profit }: { profit?: EtsyReceipt["profit"] }) {
  if (!profit) return <span className="text-gray-300" title="Kârlılık henüz hesaplanmadı">Kâr —</span>;
  if (profit.netProfit === null) {
    return <span className="text-amber-600" title="Ürün/kargo maliyeti bulunamadı — detaydan gir">Kâr: maliyet yok</span>;
  }
  const pos = profit.netProfit >= 0;
  return (
    <span
      className={clsx("rounded-full px-2 py-0.5 font-semibold", pos ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700")}
      title={`Etsy'ye göre kazanç: ${profit.etsyEarned.toFixed(2)} ${profit.currency}`}
    >
      Kâr {profit.netProfit.toFixed(2)} {profit.currency}
      {profit.margin !== null && <span className="ml-1 font-normal">(%{profit.margin.toFixed(0)})</span>}
    </span>
  );
}

function formatDate(timestamp?: number | null) {
  if (!timestamp) return "-";
  return new Date(timestamp * 1000).toLocaleDateString("tr-TR", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function formatDay(timestamp?: number | null) {
  if (!timestamp) return "-";
  return new Date(timestamp * 1000).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

// Etsy v3 hem was_* (eski) hem is_* (yeni) alanlarini dondurebiliyor;
// ayrica `status` metni tek basina belirleyici olabiliyor.
function isPaid(r: EtsyReceipt) {
  const s = (r.status || "").toLowerCase();
  return Boolean(r.was_paid || r.is_paid) || ["paid", "completed"].includes(s) || isShipped(r);
}
function isShipped(r: EtsyReceipt) {
  return Boolean(r.was_shipped || r.is_shipped) || (r.shipments?.length ?? 0) > 0;
}
function isCanceled(r: EtsyReceipt) {
  const s = (r.status || "").toLowerCase();
  return Boolean(r.was_canceled) || s === "canceled" || s === "fully_refunded";
}

// Siparisi SON GUN tamamla (Noel serisi 3/5. bolum): takip numarasi erken
// girilirse alici "kargoya verildi" bildirimi alir ama paket gunlerce
// pre-transit gorunur ve soru gelir. Gun farki: en erken beklenen sevk tarihi.
function shipDueDays(r: EtsyReceipt): number | null {
  if (!isPaid(r) || isShipped(r) || isCanceled(r)) return null;
  const dates = (r.transactions || []).map((t) => t.expected_ship_date).filter((x): x is number => !!x);
  if (!dates.length) return null;
  const due = new Date(Math.min(...dates) * 1000);
  const today = new Date();
  const day = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((day(due) - day(today)) / 86400000);
}

function ShipDueBadge({ days }: { days: number | null }) {
  if (days === null) return null;
  const [label, cls] = days < 0
    ? [`Sevk tarihi ${-days} gün geçti`, "bg-red-50 text-red-700"]
    : days === 0 ? ["Bugün tamamla", "bg-amber-100 text-amber-800"]
    : days === 1 ? ["Yarın son gün", "bg-amber-50 text-amber-700"]
    : [`Son güne ${days} gün`, "bg-gray-100 text-gray-600"];
  return <span className={clsx("rounded-full px-2 py-0.5 text-xs font-medium", cls)} title="Takip numarasını işlem süresinin son günü gir">{label}</span>;
}

function getStatusBadge(receipt: EtsyReceipt) {
  if (isCanceled(receipt)) return { label: "İptal / İade", color: "text-red-600 bg-red-50", icon: XCircle };
  if (receipt.was_delivered) return { label: "Teslim Edildi", color: "text-emerald-600 bg-emerald-50", icon: CheckCircle };
  if (isShipped(receipt)) return { label: "Gönderildi", color: "text-blue-600 bg-blue-50", icon: Truck };
  if (isPaid(receipt)) return { label: "Hazırlanacak", color: "text-yellow-600 bg-yellow-50", icon: Clock };
  return { label: "Ödenmedi", color: "text-gray-600 bg-gray-100", icon: AlertCircle };
}

// Kalemin kisisellestirme (personalization) girdileri
function personalizationOf(tx: EtsyTransaction): Array<{ label: string; value: string }> {
  return (tx.product_data || [])
    .filter((p) => (p.values || []).length > 0)
    .map((p) => ({ label: p.property_name, value: (p.values || []).join(", ") }));
}

function CopyButton({ text, title, className }: { text: string; title?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        });
      }}
      title={copied ? "Kopyalandı" : title || "Kopyala"}
      className={clsx("rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700", className)}
    >
      {copied ? <CheckCircle className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

// Urun gorseli: buyuk kucuk resim + uzerine gelince yanda genis onizleme
function ProductImage({
  tx,
  size = "h-24 w-24",
}: {
  tx: EtsyTransaction;
  size?: string;
}) {
  const src = tx.image_url || tx.image_thumb_url || null;
  const inner = src ? (
    <img src={src} alt={tx.title} className="h-full w-full object-cover" loading="lazy" />
  ) : (
    <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-5 w-5 text-gray-300" /></div>
  );

  return (
    <div className="group/img relative shrink-0">
      {tx.listing_url ? (
        <a
          href={tx.listing_url}
          target="_blank"
          rel="noreferrer"
          title="Etsy ürün sayfasını aç"
          className={clsx("block overflow-hidden rounded-lg border border-gray-200 bg-gray-50", size)}
        >
          {inner}
        </a>
      ) : (
        <div className={clsx("overflow-hidden rounded-lg border border-gray-200 bg-gray-50", size)}>{inner}</div>
      )}

      {src && (
        <div className="pointer-events-none absolute left-full top-0 z-40 ml-3 hidden group-hover/img:block">
          <img
            src={src}
            alt=""
            className="max-h-96 w-80 rounded-xl border border-gray-200 bg-white object-contain p-1 shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

// ============================================
// Item row (list + detail ortak)
// ============================================

function ItemCard({ tx, bare }: { tx: EtsyTransaction; bare?: boolean }) {
  const personalization = personalizationOf(tx);
  const color = (tx.variations || []).find((v) => /colou?r|renk/i.test(v.formatted_name))?.formatted_value ?? null;

  return (
    <div className={clsx("flex gap-3", !bare && "rounded-lg border border-gray-100 bg-white p-3")}>
      <ProductImage tx={tx} size="h-28 w-28" />

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          {/* Baslik kirpilmaz */}
          <p className="text-sm font-medium leading-snug text-gray-900">{tx.title}</p>
          <div className="shrink-0 text-right">
            <p className="text-sm font-semibold text-gray-900">{formatPrice(tx.price)}</p>
            <p className="text-[11px] text-gray-400">{tx.quantity} adet</p>
          </div>
        </div>

        {/* SKU + varyasyonlar */}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {tx.sku && (
            <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
              <Hash className="h-2.5 w-2.5" />
              {tx.sku}
              <CopyButton text={tx.sku} title="SKU kopyala" />
            </span>
          )}
          {(tx.variations || []).map((v, i) => (
            <span key={i} className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] text-indigo-700">
              <span className="text-indigo-400">{v.formatted_name}:</span> {v.formatted_value}
            </span>
          ))}
          {tx.is_digital && (
            <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[11px] text-purple-700">Dijital</span>
          )}
        </div>

        {/* Kisisellestirme — musteriden gelen metin, kopyalanabilir */}
        {personalization.length > 0 && (
          <div className="mt-1.5 space-y-0.5 rounded-lg border border-amber-100 bg-amber-50 px-2 py-1.5">
            {personalization.map((p, i) => (
              <div key={i} className="flex items-start gap-1 text-xs text-amber-900">
                <span className="shrink-0 font-medium">{p.label}:</span>
                <span className="whitespace-pre-wrap break-words">{p.value}</span>
                <CopyButton text={p.value} title="Kişiselleştirme metnini kopyala" className="shrink-0 hover:bg-amber-100" />
              </div>
            ))}
          </div>
        )}

        {/* Linkler: Etsy urunu + eslesen tasarim */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {tx.listing_url && (
            <a
              href={tx.listing_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
            >
              <Store className="h-3 w-3" /> Ürün #{tx.listing_id}
              <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
          {tx.shipping_profile && (
            <span className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] text-sky-800"
              title="Siparişin verildiği ilanın kargo profili ve çıkış yeri">
              <Truck className="h-3 w-3" /> {tx.shipping_profile.title}{tx.shipping_profile.origin ? ` · ${tx.shipping_profile.origin}` : ""}
            </span>
          )}
          {tx.listing_state && tx.listing_state !== "active" && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">{tx.listing_state}</span>
          )}

        </div>

        {/* Baski dosyalari (acik/koyu) + tedarikci urunu */}
        <OrderDesignFiles design={tx.design} alts={tx.designAlts} sku={tx.sku} color={color} supplier={tx.supplier} bigPrint={tx.bigPrint} />
        {tx.design?.customListingId && <CustomOrderPrint recId={tx.design.customListingId} personalization={personalization} color={color} bigPrint={tx.bigPrint} />}

        {/* Kalem bazli kargo/uretim bilgisi */}
        {(tx.expected_ship_date || tx.shipping_method || tx.min_processing_days != null) && (
          <div className="mt-1.5 flex flex-wrap gap-3 text-[11px] text-gray-500">
            {tx.expected_ship_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Beklenen sevk: {formatDay(tx.expected_ship_date)}
              </span>
            )}
            {tx.min_processing_days != null && (
              <span>Üretim: {tx.min_processing_days}-{tx.max_processing_days ?? tx.min_processing_days} gün</span>
            )}
            {tx.shipping_method && <span>Kargo: {tx.shipping_method}</span>}
            {tx.shipping_cost && priceValue(tx.shipping_cost) > 0 && (
              <span>Kargo ücreti: {formatPrice(tx.shipping_cost)}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Page
// ============================================

export default function EtsyOrdersPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<OrderFilter>("all");
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState<EtsyReceipt | null>(null);
  // note: aliciya kargo bildirimiyle giden mesaj (Etsy'de tek mesaj API yolu);
  // noteId: onaylanacak "kargoya verildi" taslagi
  const [trackingForm, setTrackingForm] = useState<{ receiptId: number; code: string; carrier: string; note?: string; noteId?: string } | null>(null);
  const trackingReceiptId = trackingForm?.receiptId;
  useEffect(() => {
    if (!trackingReceiptId) return;
    let alive = true;
    listBuyerMessages({ receiptId: trackingReceiptId, kind: "order_shipped", status: "draft" })
      .then((rows) => {
        const d = rows.find((r) => r.channel === "api");
        if (alive && d) setTrackingForm((f) => (f && f.receiptId === trackingReceiptId && f.note === undefined ? { ...f, note: d.body, noteId: d.id } : f));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [trackingReceiptId]);
  const limit = 25;
  // Aktif magaza sorgu anahtarina girer: magaza degisince cache karismasin
  const shopId = useActiveShopId();

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ["etsy-receipts", shopId, filter, offset],
    queryFn: () => fetchReceipts(filter, offset, limit),
  });

  const trackingM = useMutation({
    mutationFn: (params: { receiptId: number; tracking_code: string; carrier_name: string; note_to_buyer?: string; buyer_message_id?: string }) =>
      shipWithNote(params.receiptId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["etsy-receipts"] });
      queryClient.invalidateQueries({ queryKey: ["buyer-messages"] });
      setTrackingForm(null);
    },
  });

  const allReceipts = useMemo(() => data?.results || [], [data]);
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  // Sayfa ici arama: siparis no, alici, e-posta, urun basligi, SKU
  const receipts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = filter === "due" ? allReceipts.filter((r) => { const d = shipDueDays(r); return d !== null && d <= 0; }) : allReceipts;
    if (!q) return base;
    return base.filter((r) => {
      const haystack = [
        String(r.receipt_id),
        r.name,
        r.buyer_email,
        r.city,
        r.country_iso,
        ...(r.transactions || []).flatMap((t) => [t.title, t.sku || "", t.design?.name || ""]),
        ...(r.shipments || []).map((s) => s.tracking_code),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [allReceipts, search, filter]);

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <ShoppingCart className="h-6 w-6 text-orange-500" />
            Siparişler
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Etsy siparişlerinizi yönetin ({totalCount} sipariş)
          </p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ["etsy-receipts"] })}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className={clsx("h-4 w-4", isFetching && "animate-spin")} />
          Yenile
        </button>
      </div>

      {/* Filters + search */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-gray-400" />
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => { setFilter(opt.value); setOffset(0); }}
            className={clsx(
              "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
              filter === opt.value
                ? "border border-orange-200 bg-orange-100 text-orange-700"
                : "text-gray-500 hover:bg-gray-100"
            )}
          >
            {opt.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sipariş no, alıcı, ürün, SKU..."
            className="w-64 rounded-lg border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
        </div>
      </div>

      {/* Error */}
      {isError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{(error as Error).message}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      )}

      {/* Tracking Form Modal */}
      {trackingForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setTrackingForm(null)}>
          <div className="m-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-lg font-semibold">Kargo Bilgisi Ekle</h3>
            {(() => {
              const r = allReceipts.find((x) => x.receipt_id === trackingForm.receiptId);
              const d = r ? shipDueDays(r) : null;
              return d !== null && d >= 2 ? (
                <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Son güne {d} gün var. Takip numarasını erken girersen alıcıya "kargoya verildi" bildirimi gider ama paket günlerce "pre-transit" görünür ve soru gelir. Mümkünse son gün gir.
                </p>
              ) : null;
            })()}
            <div className="space-y-3">
              <TrackingFields
                code={trackingForm.code}
                carrier={trackingForm.carrier}
                onCode={(v) => setTrackingForm((f) => (f ? { ...f, code: v } : f))}
                onCarrier={(v) => setTrackingForm((f) => (f ? { ...f, carrier: v } : f))}
              />
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Alıcıya not <span className="font-normal text-gray-400">(isteğe bağlı)</span>
                </label>
                <textarea
                  value={trackingForm.note ?? ""}
                  onChange={(e) => setTrackingForm({ ...trackingForm, note: e.target.value })}
                  rows={5}
                  placeholder="Etsy'nin kargo bildirimine eklenir"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {trackingForm.noteId ? "\"Kargoya verildi\" taslağı yüklendi. " : ""}
                  Not, takip numarasıyla birlikte Etsy'nin kargo bildiriminde alıcıya gider.
                </p>
              </div>
              {trackingM.isError && (
                <p className="text-xs text-red-600">{(trackingM.error as Error).message}</p>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => {
                    if (trackingForm.code && trackingForm.carrier) {
                      rememberCarrier(trackingForm.carrier);
                      trackingM.mutate({
                        receiptId: trackingForm.receiptId,
                        tracking_code: trackingForm.code.trim(),
                        carrier_name: trackingForm.carrier.trim(),
                        ...(trackingForm.note?.trim() ? { note_to_buyer: trackingForm.note.trim(), buyer_message_id: trackingForm.noteId } : {}),
                      });
                    }
                  }}
                  disabled={!trackingForm.code || !trackingForm.carrier || trackingM.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-40"
                >
                  {trackingM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Gönder
                </button>
                <button
                  onClick={() => setTrackingForm(null)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Detail Modal */}
      {selectedReceipt && (
        <OrderDetailModal
          receipt={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
          onAddTracking={() => {
            setTrackingForm({ receiptId: selectedReceipt.receipt_id, code: "", carrier: "" });
            setSelectedReceipt(null);
          }}
        />
      )}

      {/* Empty */}
      {!isLoading && receipts.length === 0 && (
        <div className="py-16 text-center">
          <ShoppingCart className="mx-auto mb-3 h-12 w-12 text-gray-300" />
          <p className="text-gray-500">
            {search ? "Aramayla eşleşen sipariş yok" : "Bu kategoride sipariş bulunamadı"}
          </p>
        </div>
      )}

      {/* Receipts List */}
      {!isLoading && receipts.length > 0 && (
        <div className="space-y-3">
          {receipts.map((receipt) => {
            const status = getStatusBadge(receipt);
            const txs = receipt.transactions || [];
            const itemCount = txs.reduce((sum, t) => sum + (t.quantity || 1), 0);
            const address = [receipt.city, receipt.state, receipt.country_iso].filter(Boolean).join(", ");

            return (
              <div
                key={receipt.receipt_id}
                className="rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md"
              >
                {/* Ust satir: durum · siparis no · alici · ozet · aksiyonlar */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span className={clsx("flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", status.color)}>
                    <status.icon className="h-3.5 w-3.5" />
                    {status.label}
                  </span>

                  <ShipDueBadge days={shipDueDays(receipt)} />

                  <span className="flex items-center gap-0.5 text-sm font-semibold text-gray-900">
                    #{receipt.receipt_id}
                    <CopyButton text={String(receipt.receipt_id)} title="Sipariş no kopyala" />
                  </span>

                  <span className="flex items-center gap-0.5 text-sm text-gray-600">
                    {receipt.name}
                    <CopyButton text={receipt.name} title="Alıcı adını kopyala" />
                  </span>

                  {receipt.is_gift && (
                    <span className="inline-flex items-center gap-1 rounded bg-pink-50 px-1.5 py-0.5 text-[11px] text-pink-700">
                      <Gift className="h-3 w-3" /> Hediye
                    </span>
                  )}

                  <span className="ml-auto flex items-center gap-1">
                    <button
                      onClick={() => setSelectedReceipt(receipt)}
                      className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      title="Detay"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {receipt.etsy_order_url && (
                      <a
                        href={receipt.etsy_order_url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-lg p-2 text-gray-400 hover:bg-orange-50 hover:text-orange-600"
                        title="Etsy'de aç"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    {isPaid(receipt) && !isShipped(receipt) && (
                      <button
                        onClick={() => setTrackingForm({ receiptId: receipt.receipt_id, code: "", carrier: "" })}
                        className="rounded-lg p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                        title="Kargo Ekle"
                      >
                        <Truck className="h-4 w-4" />
                      </button>
                    )}
                  </span>
                </div>

                {/* Meta */}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Package className="h-3 w-3" />{itemCount} adet</span>
                  <span className="flex items-center gap-1 font-medium text-gray-700">
                    <DollarSign className="h-3 w-3" />{formatPrice(receipt.grandtotal)}
                  </span>
                  <ProfitBadge profit={receipt.profit} />
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{formatDate(receipt.create_timestamp)}</span>
                  {address && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />{address}
                      <CopyButton text={address} title="Adresi kopyala" />
                    </span>
                  )}
                  {receipt.message_from_buyer && (
                    <span className="flex items-center gap-1 text-blue-600">
                      <FileText className="h-3 w-3" /> Alıcı notu var
                    </span>
                  )}
                </div>

                {/* Mesaj gorevleri: siparis geldi -> kargo -> teslim */}
                <OrderTasks
                  receiptId={receipt.receipt_id}
                  tasks={receipt.tasks}
                  paid={isPaid(receipt)}
                  shipped={isShipped(receipt)}
                  delivered={!!receipt.was_delivered}
                  canceled={isCanceled(receipt)}
                />

                {/* Kalemler — hepsi, kirpilmadan */}
                <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
                  {txs.map((tx) => <ItemCard key={tx.transaction_id} tx={tx} bare />)}
                </div>

                {/* Kargo takip */}
                {(receipt.shipments || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-100 pt-2">
                    {receipt.shipments!.map((s, i) => {
                      const url = trackingUrl(s.carrier_name, s.tracking_code);
                      return (
                        <span key={i} className="flex items-center gap-1.5 text-xs text-blue-600">
                          <Truck className="h-3 w-3" />
                          <span className="uppercase">{s.carrier_name}</span>
                          {url ? (
                            <a href={url} target="_blank" rel="noreferrer" className="font-mono underline hover:text-blue-700">
                              {s.tracking_code}
                            </a>
                          ) : (
                            <span className="font-mono">{s.tracking_code}</span>
                          )}
                          <CopyButton text={s.tracking_code} title="Takip no kopyala" />
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Sayfa {currentPage} / {totalPages} ({totalCount} sipariş)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
              Önceki
            </button>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= totalCount}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Detail modal
// ============================================

function OrderDetailModal({
  receipt,
  onClose,
  onAddTracking,
}: {
  receipt: EtsyReceipt;
  onClose: () => void;
  onAddTracking: () => void;
}) {
  const status = getStatusBadge(receipt);
  const txs = receipt.transactions || [];
  const address = receipt.formatted_address
    ? receipt.formatted_address.split("\n").filter(Boolean)
    : [
        receipt.first_line,
        receipt.second_line,
        [receipt.city, receipt.state, receipt.zip].filter(Boolean).join(", "),
        receipt.country_iso,
      ].filter(Boolean);

  const discount = priceValue(receipt.discount_amt);
  const giftWrap = priceValue(receipt.gift_wrap_price);
  const vat = priceValue(receipt.total_vat_cost);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-100 p-5">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-gray-900">Sipariş #{receipt.receipt_id}</h2>
              <CopyButton text={String(receipt.receipt_id)} title="Sipariş no kopyala" />
              <span className={clsx("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium", status.color)}>
                <status.icon className="h-3.5 w-3.5" />
                {status.label}
              </span>
              {receipt.is_gift && (
                <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2 py-1 text-xs text-pink-700">
                  <Gift className="h-3 w-3" /> Hediye
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Oluşturma: {formatDate(receipt.create_timestamp)} · Güncelleme: {formatDate(receipt.update_timestamp)}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {receipt.etsy_order_url && (
              <a
                href={receipt.etsy_order_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Etsy
              </a>
            )}
            <button onClick={onClose} className="rounded p-1 text-2xl leading-none text-gray-400 hover:text-gray-600">
              &times;
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {/* Alici & adres & odeme */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <User className="h-3.5 w-3.5" /> Alıcı
                <CopyButton text={`${receipt.name}\n${address.join("\n")}`} title="Ad + adresi kopyala" />
              </h3>
              <p className="text-sm font-medium text-gray-900">{receipt.name}</p>
              {receipt.buyer_email && (
                <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600">
                  <Mail className="h-3 w-3" />
                  <a href={`mailto:${receipt.buyer_email}`} className="hover:underline">{receipt.buyer_email}</a>
                  <CopyButton text={receipt.buyer_email} />
                </p>
              )}
              <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-600">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                <div>
                  {address.map((line, i) => <p key={i}>{line}</p>)}
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <CreditCard className="h-3.5 w-3.5" /> Ödeme & Durum
              </h3>
              <dl className="space-y-1 text-xs">
                {receipt.status && (
                  <div className="flex justify-between"><dt className="text-gray-500">Etsy durumu</dt><dd className="text-gray-800">{receipt.status}</dd></div>
                )}
                {receipt.payment_method && (
                  <div className="flex justify-between"><dt className="text-gray-500">Yöntem</dt><dd className="text-gray-800">{receipt.payment_method}</dd></div>
                )}
                {receipt.payment_email && (
                  <div className="flex justify-between"><dt className="text-gray-500">Ödeme e-posta</dt><dd className="truncate text-gray-800">{receipt.payment_email}</dd></div>
                )}
                <div className="flex justify-between"><dt className="text-gray-500">Ödendi</dt><dd>{isPaid(receipt) ? "Evet" : "Hayır"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Gönderildi</dt><dd>{isShipped(receipt) ? "Evet" : "Hayır"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Teslim</dt><dd>{receipt.was_delivered ? "Evet" : "Hayır"}</dd></div>
                <div className="flex justify-between"><dt className="text-gray-500">Alıcı ID</dt><dd className="font-mono">{receipt.buyer_user_id}</dd></div>
              </dl>
            </div>
          </div>

          {/* Urunler */}
          <div>
            <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <Package className="h-3.5 w-3.5" /> Ürünler ({txs.length})
            </h3>
            <div className="space-y-2">
              {txs.map((tx) => <ItemCard key={tx.transaction_id} tx={tx} />)}
            </div>
          </div>

          {/* Kargo */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <Truck className="h-3.5 w-3.5" /> Kargo
              </h3>
              {isPaid(receipt) && !isShipped(receipt) && (
                <button
                  onClick={onAddTracking}
                  className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                >
                  <Send className="h-3 w-3" /> Kargo bilgisi ekle
                </button>
              )}
            </div>
            {receipt.shipments && receipt.shipments.length > 0 ? (
              <div className="space-y-1.5">
                {receipt.shipments.map((s, i) => {
                  const url = trackingUrl(s.carrier_name, s.tracking_code);
                  return (
                    <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 bg-white px-3 py-2 text-xs">
                      <span className="font-medium uppercase text-gray-700">{s.carrier_name}</span>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="font-mono text-blue-600 underline">
                          {s.tracking_code}
                        </a>
                      ) : (
                        <span className="font-mono text-gray-700">{s.tracking_code}</span>
                      )}
                      <CopyButton text={s.tracking_code} />
                      {s.shipment_notification_timestamp && (
                        <span className="text-gray-400">Bildirim: {formatDate(s.shipment_notification_timestamp)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-400">Henüz kargo bilgisi girilmemiş</p>
            )}
          </div>

          {/* Notlar */}
          {receipt.message_from_buyer && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
              <p className="mb-1 text-xs font-medium text-blue-800">Alıcı Notu</p>
              <p className="whitespace-pre-wrap text-sm text-blue-700">{receipt.message_from_buyer}</p>
            </div>
          )}
          {receipt.message_from_seller && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="mb-1 text-xs font-medium text-gray-700">Satıcı Notu</p>
              <p className="whitespace-pre-wrap text-sm text-gray-600">{receipt.message_from_seller}</p>
            </div>
          )}
          {receipt.is_gift && receipt.gift_message && (
            <div className="rounded-lg border border-pink-100 bg-pink-50 p-3">
              <p className="mb-1 text-xs font-medium text-pink-800">
                Hediye Mesajı{receipt.gift_sender ? ` (${receipt.gift_sender})` : ""}
              </p>
              <p className="whitespace-pre-wrap text-sm text-pink-700">{receipt.gift_message}</p>
            </div>
          )}

          {/* Iadeler */}
          {receipt.refunds && receipt.refunds.length > 0 && (
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <RotateCcw className="h-3.5 w-3.5" /> İadeler
              </h3>
              <div className="space-y-1.5">
                {receipt.refunds.map((r, i) => (
                  <div key={i} className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                    <div className="flex justify-between">
                      <span className="font-medium">{formatPrice(r.amount)}</span>
                      <span>{formatDate(r.created_timestamp)}</span>
                    </div>
                    {(r.reason || r.note_from_issuer) && (
                      <p className="mt-0.5">{[r.reason, r.note_from_issuer].filter(Boolean).join(" — ")}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <OrderProfitSection receiptId={receipt.receipt_id} />
        </div>

        {/* Totals footer */}
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-gray-500">Ara Toplam</dt><dd>{formatPrice(receipt.subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Kargo</dt><dd>{formatPrice(receipt.total_shipping_cost)}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-500">Vergi</dt><dd>{formatPrice(receipt.total_tax_cost)}</dd></div>
            {vat > 0 && <div className="flex justify-between"><dt className="text-gray-500">KDV</dt><dd>{formatPrice(receipt.total_vat_cost)}</dd></div>}
            {giftWrap > 0 && <div className="flex justify-between"><dt className="text-gray-500">Hediye paketi</dt><dd>{formatPrice(receipt.gift_wrap_price)}</dd></div>}
            {discount > 0 && (
              <div className="flex justify-between text-emerald-600"><dt>İndirim</dt><dd>-{formatPrice(receipt.discount_amt)}</dd></div>
            )}
            <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-semibold text-gray-900">
              <dt>Toplam</dt><dd>{formatPrice(receipt.grandtotal)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
